import TextField, { TextFieldProps } from "@mui/material/TextField";
import * as React from "react";

type NumberFieldProps = Omit<
  TextFieldProps,
  "type" | "value" | "onChange" | "error" | "helperText"
> & {
  value: number | null;
  onChange: (value: string) => void;
  minValue?: number;
  maxValue?: number;
  allowNegative?: boolean;
  hasError?: boolean;
  errorMessage?: React.ReactNode;
};

export function NumberField({
  value,
  onChange,
  minValue = 0,
  maxValue,
  allowNegative = false,
  hasError,
  errorMessage,
  onFocus,
  onWheel,
  inputProps,
  ...rest
}: NumberFieldProps) {
  const isNegative = value !== null && value < 0;
  const isBelowMin = value !== null && value < minValue;
  const isAboveMax = value !== null && maxValue != null && value > maxValue;

  const error = Boolean(
    hasError || (!allowNegative && isNegative) || isBelowMin || isAboveMax,
  );

  const helperText = hasError
    ? errorMessage
    : isBelowMin
    ? `Value can't be less than ${minValue}`
    : isAboveMax
    ? `Value can't be greater than ${maxValue}`
    : !allowNegative && isNegative
    ? "Value can't be less than 0"
    : null;

  return (
    <TextField
      {...rest}
      type="number"
      value={value ?? ""}
      error={error}
      helperText={helperText}
      inputProps={{
        min: allowNegative ? minValue : Math.max(minValue, 0),
        max: maxValue,
        ...inputProps,
      }}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      onFocus={(e) => {
        e.currentTarget.select();
        onFocus?.(e);
      }}
      // Prevent scroll-wheel from changing the number while focused
      onWheel={(e) => {
        (e.target as HTMLElement).blur();
        onWheel?.(e);
      }}
      InputLabelProps={{
        ...rest.InputLabelProps,
        shrink: value !== null && value !== undefined,
      }}
    />
  );
}
